/**
 * Iconos.tsx — los siete iconos de navegación aprobados en la Fase 0.
 *
 * Son los de `design/assets/icons/`, dibujados a propósito para este producto:
 * el de asientos es un libro mayor con su columna de debe y haber, no un
 * cuaderno genérico. Por eso no se sustituyen por los de `lucide-react`, que
 * sí se usa para todo lo demás.
 *
 * Van sin `aria-label`: el icono acompaña a un texto que ya dice lo mismo, y
 * repetirlo hace que un lector de pantalla lo lea dos veces.
 */
import type { SVGProps } from 'react';

const COMUNES: SVGProps<SVGSVGElement> = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
};

export type Icono = (p: SVGProps<SVGSVGElement>) => React.ReactElement;

export const IconoTablero: Icono = (p) => (
  <svg {...COMUNES} {...p}>
    <rect x="3" y="3" width="7.5" height="11" rx="1.5" />
    <rect x="13.5" y="3" width="7.5" height="6" rx="1.5" />
    <rect x="13.5" y="12.5" width="7.5" height="8.5" rx="1.5" />
    <rect x="3" y="17" width="7.5" height="4" rx="1.5" />
  </svg>
);

export const IconoAsientos: Icono = (p) => (
  <svg {...COMUNES} {...p}>
    <rect x="3.25" y="3.25" width="17.5" height="17.5" rx="2.5" />
    <path d="M3.25 8.75h17.5M12 8.75V20.75M6.5 13h2.75M14.75 16.5h2.75" />
  </svg>
);

export const IconoFacturas: Icono = (p) => (
  <svg {...COMUNES} {...p}>
    <path d="M6 2.75h8.5L18.25 6.5V21L15.5 19.5 12.75 21 10 19.5 7.25 21 6 20.25V2.75Z" />
    <path d="M14.25 2.9V6.5h3.6M9 10h6M9 13.5h4" />
  </svg>
);

export const IconoReportes: Icono = (p) => (
  <svg {...COMUNES} {...p}>
    <path d="M3.75 3.5v15.25a1.5 1.5 0 0 0 1.5 1.5H20.5M8 17V12M12.25 17V8M16.5 17v-6.5M20.75 17V5.5" />
  </svg>
);

export const IconoIntegraciones: Icono = (p) => (
  <svg {...COMUNES} {...p}>
    <rect x="2.75" y="8.25" width="6.5" height="7.5" rx="1.75" />
    <rect x="14.75" y="8.25" width="6.5" height="7.5" rx="1.75" />
    <path d="M9.25 12h1.5M13.25 12h1.5" />
    <circle cx="12" cy="12" r="1.25" />
  </svg>
);

export const IconoUsuarios: Icono = (p) => (
  <svg {...COMUNES} {...p}>
    <circle cx="9.25" cy="8" r="3.25" />
    <path d="M3.5 19.5a5.75 5.75 0 0 1 11.5 0M16 5.1a3.25 3.25 0 0 1 0 5.8M17.5 14.6a5.75 5.75 0 0 1 3 4.9" />
  </svg>
);

export const IconoConfiguracion: Icono = (p) => (
  <svg {...COMUNES} {...p}>
    <path d="M3.5 7h5.25M12.25 7H20.5M3.5 12h11.25M18.25 12H20.5M3.5 17h3.25M10.25 17H20.5" />
    <circle cx="10.5" cy="7" r="1.75" />
    <circle cx="16.5" cy="12" r="1.75" />
    <circle cx="8.5" cy="17" r="1.75" />
  </svg>
);

/**
 * El isotipo: dos corchetes con el signo de más y los dos puntos del balance.
 * `tono` existe porque sobre navy los corchetes van en blanco y sobre papel en
 * tinta — es el mismo dibujo, no dos logos.
 */
export function Isotipo({ tono = 'claro', ...p }: SVGProps<SVGSVGElement> & { tono?: 'claro' | 'oscuro' }) {
  const trazo = tono === 'oscuro' ? 'var(--on-dark)' : 'var(--ink)';
  return (
    <svg viewBox="0 0 120 120" aria-hidden="true" focusable="false" {...p}>
      <path d="M47 28 H29 V92 H47" fill="none" stroke={trazo} strokeWidth="8" strokeLinecap="square" />
      <path d="M73 28 H91 V92 H73" fill="none" stroke={trazo} strokeWidth="8" strokeLinecap="square" />
      <g fill={tono === 'oscuro' ? 'var(--acc-vivid)' : 'var(--acc-ink)'}>
        <rect x="47" y="49" width="26" height="5" rx="1.5" />
        <rect x="57.5" y="54" width="5" height="26" rx="1.5" />
        <circle cx="51.6" cy="64" r="2.4" />
        <circle cx="68.4" cy="71" r="2.4" />
      </g>
    </svg>
  );
}
